import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatDecimal, formatUnit } from '@/common/utils/formatters'
import { UpdateContractProductCategoryModal } from '@/features/publicCompanyLeadsFunnel/components/UpdateContractProductCategoryModal'
import { useDeletePublicCompanyLeadContractProduct } from '@/features/publicCompanyLeadsFunnel/hooks/useDeletePublicCompanyLeadContractProduct'
import { useQueryClient } from '@tanstack/react-query'
import { BoxesIcon, FileBoxIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { ContractProductCategoryData, PublicCompanyLeadData } from '../types/publicCompanyLead'
import { ContractProductCategoriesProductItem } from './ContractProductCategoriesProductItem'

interface ContractProductCategoryItemProps {
  lead?: PublicCompanyLeadData
  productCategoryIndex: number
  contractProductCategory: ContractProductCategoryData
  editable?: boolean
}

export const ContractProductCategoryItem = ({
  lead,
  productCategoryIndex,
  contractProductCategory,
  editable = true,
}: ContractProductCategoryItemProps) => {
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { mutate: deletePublicCompanyLeadContract } = useDeletePublicCompanyLeadContractProduct({
    publicCompanyLeadId: lead?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', lead?.id] })
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || false)
    },
  })

  return (
    <Box className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="border-s border-sky-300 px-3 py-2 flex">
          <Badge
            Icon={BoxesIcon}
            iconProps={{ className: 'size-5', strokeWidth: 1 }}
            text={contractProductCategory?.product_category?.display_name}
            overwriteClassName
            valueDataTestid={`contract-product-category-${productCategoryIndex}-badge`}
          />
        </div>

        {editable && (
          <div className="flex items-center gap-3">
          <UpdateContractProductCategoryModal
            lead={lead}
            contractProductCategory={contractProductCategory}
            productCategoryIndex={productCategoryIndex}
          />

          <Button
            className="px-2"
            variant="outline-error"
            onClick={() => deletePublicCompanyLeadContract(contractProductCategory.id)}
          >
            <Trash2Icon strokeWidth={1} />
          </Button>
        </div>
        )}
      </div>

      {lead?.contract?.require_product_credit_quota && (
        <div className="flex gap-4">
          <ResumeItem
            valueDataTestid={`contract-product-category-${productCategoryIndex}-value_in_currency`}
            label="Cota total em valor"
            value={
              contractProductCategory.global_value_in_currency
                ? formatCurrency(Number(contractProductCategory.global_value_in_currency))
                : ''
            }
            labelClassName="text-white-400"
            valueClassName="text-md"
          />

          <ResumeItem
            valueDataTestid={`contract-product-category-${productCategoryIndex}-value_in_unit_for_consumption`}
            label="Cota total em quantidade"
            value={
              contractProductCategory.global_value_in_unit_for_consumption
                ? formatUnit(formatDecimal(Number(contractProductCategory.global_value_in_unit_for_consumption), 0), 'item')
                : ''
            }
            labelClassName="text-white-400"
            valueClassName="text-md"
          />
        </div>
      )}

      {!!lead?.contract?.contract_product_categories?.[productCategoryIndex]?.contract_product_category_products
        ?.length && (
          <div className="flex flex-col gap-4">
            {!lead?.contract?.require_product_credit_quota && (
              <Box className="p-4 flex gap-3 flex-wrap">
                {lead?.contract?.contract_product_categories?.[
                  productCategoryIndex
                ]?.contract_product_category_products?.map((contractProductCategoryProduct, index) => (
                  <Badge
                    Icon={FileBoxIcon}
                    iconProps={{ className: 'size-5', strokeWidth: 1 }}
                    text={contractProductCategoryProduct?.product?.display_name}
                    className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
                    overwriteClassName
                    contentClassName="px-3 py-2"
                    valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-badge`}
                  />
                ))}
              </Box>
            )}

            {lead?.contract?.require_product_credit_quota &&
              lead?.contract?.contract_product_categories?.[
                productCategoryIndex
              ]?.contract_product_category_products?.map((contractProductCategoryProduct, index) => (
                <ContractProductCategoriesProductItem
                  key={contractProductCategoryProduct.id}
                  index={index}
                  productCategoryIndex={productCategoryIndex}
                  contractProductCategoryProduct={contractProductCategoryProduct}
                />
              ))}
          </div>
        )}

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </Box>
  )
}
